import { getBackendBoolean, nullIfBackendEmptyId } from '../backendScalars';

export const normalizeProductUnitRelations = (result) => {
    if (!result) return [];
    if (Array.isArray(result)) return result;
    return Array.isArray(result?.data) ? result.data : [];
};

export const getProductUnitRelationSymbol = (relation) =>
    String(
        relation?.unitOfMeasure?.symbol ||
            relation?.unitOfMeasure?.simbolo ||
            relation?.unitOfMeasure?.strSimbolo ||
            '',
    ).trim();

export const getBaseProductUnitRelation = (relations) => {
    const normalizedRelations = normalizeProductUnitRelations(relations);
    return (
        normalizedRelations.find(
            (relation) =>
                getBackendBoolean(relation?.isBase) && getBackendBoolean(relation?.isActive),
        ) ||
        normalizedRelations.find((relation) => getBackendBoolean(relation?.isBase)) ||
        null
    );
};

export const getProductBaseUnitRelation = (productOrRelations) => {
    if (Array.isArray(productOrRelations)) {
        return getBaseProductUnitRelation(productOrRelations);
    }
    return getBaseProductUnitRelation(productOrRelations?.productUnitRelations);
};

export const getProductBaseUnitSymbol = (productOrRelations) =>
    getProductUnitRelationSymbol(getProductBaseUnitRelation(productOrRelations));

/** Row id for a product–UoM relation (`idRelation` / `id` from API). */
export const getProductUnitRelationRowId = (relation) => {
    if (!relation) return '';
    const raw = relation.id ?? relation.Id;
    return raw !== null && raw !== undefined && raw !== '' ? String(raw) : '';
};

/**
 * Resolves `productUnitRelations[]` from `idRelationUoM`: prefers relation row id; falls back to
 * catalog `unitOfMeasure.id` for legacy lines that stored the UoM id.
 */
export const findProductUnitRelationByStoredUomField = (relations, storedId) => {
    const raw = toRelationUomIdString(storedId);
    const sid = nullIfBackendEmptyId(raw || null);
    if (!sid || !Array.isArray(relations)) return null;
    const str = String(sid);
    const byRelationId = relations.find((r) => getProductUnitRelationRowId(r) === str);
    if (byRelationId) return byRelationId;
    return relations.find((r) => String(r.unitOfMeasure?.id) === str) || null;
};

/** Relation row for the line’s selected UoM relation, or explicit `unitOfMeasureRelation`. */
export const getSalesOrderLineUnitOfMeasureRelation = (line) => {
    if (!line) return null;
    if (line.unitOfMeasureRelation) return line.unitOfMeasureRelation;
    const raw = toRelationUomIdString(line.idRelationUoM);
    const cleanId = nullIfBackendEmptyId(raw || null);
    if (!cleanId) return null;
    return findProductUnitRelationByStoredUomField(line.productUnitRelations || [], cleanId);
};

export const toRelationUomIdString = (raw) => {
    if (raw === null || raw === undefined || raw === '') return '';
    if (typeof raw === 'object') {
        const inner = raw.value ?? raw.id;
        return inner !== null && inner !== undefined && inner !== '' ? String(inner) : '';
    }
    return String(raw);
};

/** Relation row label: `name (symbol)` like product UoM relations UI, e.g. "Litronas (L)". */
export const formatProductUnitRelationOptionLabel = (relation) => {
    if (!relation) return '';
    const relName = String(relation.name ?? '').trim();
    const uom = relation.unitOfMeasure;
    const sym = getProductUnitRelationSymbol(relation);
    if (relName && sym) return `${relName} (${sym})`;
    if (relName) return relName;
    if (sym) return sym;
    return uom?.descripcion || uom?.description || '';
};

export const getProductBaseUnitDisplayLabel = (productOrRelations) =>
    formatProductUnitRelationOptionLabel(getProductBaseUnitRelation(productOrRelations));

/** Display string for a sales order line UoM when `productUnitRelations` is present on the row. */
export const getSalesOrderLineUomDisplayLabel = (data) => {
    if (!data) return '';
    const match = getSalesOrderLineUnitOfMeasureRelation(data);
    if (match) return formatProductUnitRelationOptionLabel(match);
    const uom = data.unitOfMeasure;
    if (uom) {
        const desc = String(uom.descripcion || uom.description || '').trim();
        const sym = String(uom.symbol || uom.simbolo || uom.strSimbolo || '').trim();
        if (desc && sym) return `${desc} (${sym})`;
        if (desc) return desc;
        if (sym) return sym;
    }
    return '';
};

/**
 * Mirrors `OrdersManager.getLineEffectiveUnitPrice`: returns the per-unit price to
 * multiply by quantity in line totals.
 *  - If the relation flags `useSpecialPrice` and provides `specialPrice`, returns
 *    the special price (the line's stored `price` is already overwritten with it).
 *  - Otherwise returns `price * factor` (factor defaults to 1 when missing).
 */
export const getEffectiveUnitPriceFromRelation = (price, relation) => {
    const basePrice = Number(String(price ?? 0).replace(',', '.'));
    const safePrice = Number.isFinite(basePrice) ? basePrice : 0;
    if (!relation) return safePrice;
    if (getBackendBoolean(relation.useSpecialPrice) && relation.specialPrice != null) {
        const sp = Number(String(relation.specialPrice).replace(',', '.'));
        return Number.isFinite(sp) ? sp : safePrice;
    }
    const parsedFactor = Number(String(relation.factor ?? 1).replace(',', '.'));
    const factor = Number.isFinite(parsedFactor) && parsedFactor > 0 ? parsedFactor : 1;
    return safePrice * factor;
};

/** True when the line’s selected UoM relation is inactive or missing from product relations (historical/orphan). */
export const isSalesOrderLineUomInactive = (data) => {
    if (!data) return false;
    const raw = toRelationUomIdString(data.idRelationUoM);
    const currentId = nullIfBackendEmptyId(raw || null) || '';
    if (!currentId) return false;
    const match = getSalesOrderLineUnitOfMeasureRelation(data);
    if (match) return !getBackendBoolean(match.isActive);
    return true;
};
